import { useCallback, useEffect, useRef, useState } from "react";

export function useHorizontalScroll(scrollBy: number = 100) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<
    "start" | "between" | "end" | "full"
  >("full");

  const updateScrollState = (scrollLeft?: number) => {
    const el = scrollRef.current;

    if (el) {
      const currentScrollLeft = scrollLeft ?? el.scrollLeft;
      const isFull = el.scrollWidth <= el.clientWidth;
      const isStart = currentScrollLeft === 0;
      const isEnd = currentScrollLeft + el.clientWidth >= el.scrollWidth;

      let newState: "start" | "between" | "end" | "full";

      if (isFull) {
        newState = "full";
      } else if (isStart) {
        newState = "start";
      } else if (isEnd) {
        newState = "end";
      } else {
        newState = "between";
      }

      setScrollState(newState);
    }
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", () => updateScrollState());
    return () =>
      window.removeEventListener("resize", () => updateScrollState());
  }, [scrollRef.current]);

  const scrollLeft = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const current = el.scrollLeft;

      // Go to the previous multiple of scrollBy
      const prevMultiple = Math.floor((current - 1) / scrollBy) * scrollBy;

      el.scrollTo({
        left: prevMultiple,
        behavior: "smooth",
      });

      updateScrollState(prevMultiple);
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const current = el.scrollLeft;

      // Find the next multiple of scrollBy
      const nextMultiple = Math.ceil((current + 1) / scrollBy) * scrollBy;

      el.scrollTo({
        left: nextMultiple,
        behavior: "smooth",
      });

      updateScrollState(nextMultiple);
    }
  }, []);

  const resetScroll = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;

      el.scrollTo({
        left: 0,
        behavior: "smooth",
      });

      updateScrollState(0);
    }
  }, []);

  return { scrollRef, scrollLeft, scrollRight, resetScroll, scrollState };
}
